import type Context from '@/context';
import { ProjectModel } from './ProjectModel';
import { ChipModel } from './ChipModel';
import { ChipInstanceModel } from './ChipInstanceModel';
import { LogicCircuitModel } from './LogicCircuitModel';
import { LogicGraph } from './logic/LogicGraph';
import { GraphManager } from './logic/GraphManager';
import { RuntimeBindings } from './logic/RuntimeBindings';

type HookSet = Map<string, PropertyDescriptor>;
function hooks(prototype: object, names: string[]): HookSet {
  return new Map(
    names.map((name) => [name, Object.getOwnPropertyDescriptor(prototype, name)!]),
  );
}
function unchanged(value: object, prototype: object, expected: HookSet): boolean {
  for (const [name, native] of expected) {
    const actual =
      Object.getOwnPropertyDescriptor(value, name) ??
      Object.getOwnPropertyDescriptor(prototype, name);
    if (
      !actual ||
      actual.value !== native.value ||
      actual.get !== native.get ||
      actual.set !== native.set
    )
      return false;
  }
  return true;
}
const projectHooks = hooks(ProjectModel.prototype, [
  'captureSnapshot',
  'dto',
  'observeSnapshotInputs',
  'trackSnapshotDependencies',
]);
const chipHooks = hooks(ChipModel.prototype, [
  'captureSnapshot',
  'dto',
  'snapshotMetadata',
  'observeSnapshotInputs',
  'trackSnapshotDependencies',
]);
const instanceHooks = hooks(ChipInstanceModel.prototype, [
  'captureSnapshot',
  'toDto',
  'dto',
  'snapshotMetadata',
  'observeSnapshotInputs',
  'trackSnapshotDependencies',
]);

/** Identify the built-in snapshot contract without running observation, flushing
 * visuals/rebuilds, reading nodes, or evaluating custom serialization getters. */
export function hasNativeProjectSnapshotHooks(context: Context): boolean {
  const project = context.project;
  if (
    !ProjectModel.isNativeSnapshotModel(project) ||
    !unchanged(project, ProjectModel.prototype, projectHooks)
  )
    return false;
  const chips = context.chipStore.chips;
  const templates = new Map<string, import('@/interfaces/models').ILogicGraph | null>();
  for (const chip of chips) {
    if (
      !ChipModel.isNativeSnapshotModel(chip) ||
      !unchanged(chip, ChipModel.prototype, chipHooks)
    )
      return false;
    // These are native observable references. Do not request logicGraph or a
    // snapshot: the manager's stored graph is enough to inspect hook integrity.
    const logic = chip.logicCircuit;
    if (!LogicCircuitModel.isNativeSnapshotModel(logic)) return false;
    const manager = logic.graphManager;
    if (!GraphManager.isNativeSnapshotModel(manager)) return false;
    const graph = Object.getOwnPropertyDescriptor(manager, 'graph');
    if (
      !graph ||
      !('value' in graph) ||
      !LogicGraph.supportsEditingOwnership(graph.value)
    )
      return false;
    const template = chip.templateGraph;
    if (template && !LogicGraph.supportsEditingOwnership(template)) return false;
    templates.set(chip.id, template);
  }
  for (const chip of chips) {
    const instances = [chip.editingInstance, ...chip.logicCircuit.chipInstances.values()];
    for (const instance of instances) {
      if (
        !ChipInstanceModel.isNativeSnapshotModel(instance) ||
        !unchanged(instance, ChipInstanceModel.prototype, instanceHooks) ||
        !RuntimeBindings.hasNativeSnapshotHooks(instance.templateToInstanceIdMap) ||
        !LogicGraph.supportsEditingOwnership(instance.logicGraph)
      )
        return false;
      const template = templates.get(instance.chipId);
      if (template && !LogicGraph.supportsEditingOwnership(template)) return false;
    }
  }
  return true;
}
